import { compact } from 'lodash'
import moment from 'moment/moment'

import { TagProps } from '../components/app/design/Tag'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { mergeFk } from '../utils'
import { formatTimeStamp } from '../utils/dates'
import { currencyFloat } from '../utils/numbers'
import { BaseRecord } from './BaseRecord'
import { Booking } from './Booking'
import { Deposit } from './Deposit'
import { Estimate } from './Estimate'
import { Gift } from './Gift'
import { Invoice } from './Invoice'
import { Option } from './Option'
import { Order } from './Order'
import { Package } from './Package'
import { Royalty } from './Royalty'
import { FKType } from './SQLBuilder'
import { Source } from './Source'
import { Subscription } from './Subscription'
import { Transactable } from './Transactable'
import { Transaction } from './Transaction'

type PaymentConstructorArgs = Omit<ConstructorArgs<() => Payment>, 'pos' | 'owed_amount'> & {
    is_pos: boolean | null
    owed_amount: string | number | null
}

export class Payment extends Transaction implements Transactable {
    readonly recordType = 'payment'

    readonly iTransactable = true

    tip?: number
    allowSurcharge?: boolean // set in UI and used to pay surcharge for business (not stored)
    serverAmount?: number // the amount field from the server, which is for some reason different than what we're calculating client-side
    surcharge?: number
    paidDeposit?: number // set in UI when charging for bookings/estimates with an already paid deposit
    overrideTotal?: boolean // only set in UI during complete & charge (i.e. total not auto-calculated from line totals)
    limitSurcharge?: boolean // only set when super-admin sends a Pro payment via ACH (not stored - only used for preview)
    fee?: number
    ship?: number // set in the UI during complete & charge for orders
    shipTaxable?: boolean // set in the UI during complete & charge for orders
    shipPricing?: string // set in the UI during complete & charge for orders
    parcelCount?: number // Integer // set in the UI during complete & charge for orders when carrier pricing is enabled
    parcel?: string // set in the UI during complete & charge for orders when carrier pricing is enabled
    carrier?: string // set in the UI during complete & charge for orders when carrier pricing is enabled
    delivery?: string // set in the UI during complete & charge for orders when carrier pricing is enabled
    shipment?: string // set in the UI during complete & charge for orders when carrier pricing is enabled
    payout?: number
    refundedAmount?: number
    appearsAs?: string
    memo?: string
    type?: string
    name?: string // set during card reader checkout for sending receipts
    phone?: string // set during card reader checkout for sending receipts
    email?: string // set during card reader checkout for sending receipts
    sourceType?: string
    refundReason?: string
    refundDetails?: string
    refundType?: string // set during payment refund checkout for specifying refund type (full|net|partial)
    refundAmount?: number // set during payment refund checkout for specifying refund type (full|net|partial)

    order?: Order
    checkoutGift?: Gift // applied gift certificate used during checkout
    pos?: boolean
    sendReceipt: boolean = false // mark paid boolean
    intent?: string // set during card reader checkout for collecting payment
    longitude?: number
    latitude?: number // set during card reader checkout for fraud detection

    markPaidDate?: moment.Moment

    readonly refundedDate?: moment.Moment
    readonly depositedDate?: moment.Moment
    readonly availableOnDate?: moment.Moment
    readonly chargeBackDate?: moment.Moment
    readonly chargeBackAmount?: number

    readonly instantDate?: moment.Moment
    readonly releasedDate?: moment.Moment

    private readonly _anonymous: boolean = false

    readonly sourceDisplay?: string

    static readonly ACH_SURCHARGE_RATE = 0.01
    static readonly PAYMENT_SURCHARGE_RATE = 0.03
    static readonly POS_CHARGE_LIMIT = 25000
    static readonly ACH_PROCESSING_FEE_LIMIT = 1

    constructor(data?: Partial<PaymentConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.fee = this.propertyFromCurrencyArg(data.fee)
        this.tip = this.propertyFromCurrencyArg(data.tip)
        this.serverAmount = this.propertyFromCurrencyArg(data.amount)
        this.allowSurcharge = data.allow_surcharge ?? false
        this.surcharge = this.propertyFromCurrencyArg(data.surcharge)
        this.payout = this.propertyFromCurrencyArg(data.payout)
        this.appearsAs = data.appears_as ?? undefined
        this.refundReason = data.refund_reason ?? undefined
        this.refundDetails = data.refund_details ?? undefined
        this.refundedAmount = this.propertyFromCurrencyArg(data.refunded_amount)
        this.memo = data.memo ?? undefined
        this.type = data.type ?? undefined
        this.sourceType = data.source_type ?? undefined
        this.pos = data.is_pos ?? false
        this.sendReceipt = data.send_receipt ?? false

        this.markPaidDate = this.propertyFromDateArg(data.mark_paid_date)
        this.refundedDate = this.propertyFromTimestampArg(data.refunded_date)
        this.depositedDate = this.propertyFromTimestampArg(data.deposited_date)
        this.availableOnDate = this.propertyFromTimestampArg(data.available_on_date)
        this.chargeBackDate = this.propertyFromTimestampArg(data.charge_back_date)
        this.chargeBackAmount = this.propertyFromCurrencyArg(data.charge_back_amount)

        this.instantDate = this.propertyFromDateArg(data.instant_date)
        this.releasedDate = this.propertyFromTimestampArg(data.released_date)

        this._anonymous = data.anonymous ?? false

        this.sourceDisplay = data.source_display ?? undefined

        this.paidDeposit = this.propertyFromCurrencyArg(data.paid_deposit)

        if (data.package_ref && typeof data.package_ref !== 'string') {
            this.setForeignRecord('package', data.package_ref)
        }
    }

    clone(overrides?: Partial<PaymentConstructorArgs>): Payment {
        return new Payment({
            ...this.serializeForClone(),
            is_pos: this.pos,
            owed_amount: this.owedAmount,
            ...overrides,
        })
    }

    get amount(): number | undefined {
        let total = this.total ?? 0
        if (this.discount && this.discount > 0) total = total - (this.discountAmount ?? 0)
        if (this.paidDeposit && this.paidDeposit > 0) total = Math.max(0, total - this.paidDeposit)
        if (this.surcharge && this.surcharge > 0) total = total + this.surcharge
        if (this.tip && this.tip > 0) total = total + this.tip
        return total
    }

    get aPackage(): Package | undefined {
        return this.packageRef
    }

    set aPackage(aPackage: Package | undefined) {
        this.packageRef = aPackage
    }

    get paidAmount(): number | undefined {
        return this.amount
    }

    get owedAmount(): undefined {
        return undefined
    }

    get instant(): boolean {
        if (this.instantDate && this.availableOnDate) return moment().isAfter(this.instantDate)
        else if (this.availableOnDate && this.releasedDate)
            return moment().isAfter(this.availableOnDate)
        return false
    }

    get anonymous(): boolean {
        if (this.id) return this._anonymous
        if (!this.id && this.client) return this.client.anonymous
        return false
    }

    get sourceString(): string | undefined {
        if (this.id) return this.sourceDisplay
        return undefined
    }

    get statusString(): string {
        if (this.refundedDate)
            return `Refunded ${currencyFloat(this.refundedAmount)} on ${this.refundedDate.format('MMM D, YYYY')}`
        else if (this.chargeBackDate)
            return `Disputed ${currencyFloat(this.chargeBackAmount)} on ${this.chargeBackDate.format('MMM D, YYYY')}`
        else if (this.cancelledDate)
            return `Canceled on ${this.cancelledDate.format('MMM D, YYYY')}`
        if (this.refundReason && this.refundReason.length > 0) return 'Refund requested'
        return this.createdDate ? `Paid ${this.createdDate.format('MMM D, YYYY')}` : 'Unknown'
    }

    get statuses(): {
        title: string
        date: moment.Moment | undefined
        intent: TagProps['intent']
    }[] {
        return compact([
            {
                title: 'Paid',
                date: this.createdDate,
                intent: 'info',
            },
            {
                title: 'Sent',
                date: this.depositedDate,
                intent: 'info',
            },
            {
                title: 'Received',
                date: this.paidDate,
                intent: 'success',
            },
            this.refundedDate && {
                title: 'Refunded',
                date: this.refundedDate,
                intent: 'danger',
            },
        ])
    }

    get statusTag(): { label: string; intent: TagProps['intent'] } | null {
        const lastActiveStatus = this.statuses.findLast((status) => status.date !== undefined)
        if (!lastActiveStatus) {
            return null
        }

        return { label: lastActiveStatus.title, intent: lastActiveStatus.intent }
    }

    get gratuityAmount(): number | undefined {
        if (this.gratuity && this.gratuity > 0 && this.type !== 'deposit') {
            let gratuity = this.gratuity
            if (this.gratuityType === 'percent')
                gratuity = currencyFloat(
                    (this.gratuity * ((this.amount ?? 0) + (this.paidDeposit ?? 0))) / 100
                )
            return gratuity
        }
        return undefined
    }

    get shipAmount(): number | undefined {
        if (this.ship === 0) return 0

        let price = 0
        const pricing = this.shipPricing
        if (pricing === 'weight') {
            const totalWeight = this.weight
            if (totalWeight !== undefined) price = (this.ship ?? 0) * totalWeight
        }
        else if (pricing === 'unit') {
            const totalUnits = this.units
            if (totalUnits !== undefined) price = (this.ship ?? 0) * totalUnits
        }
        else if (pricing === 'percent') {
            price = currencyFloat(((this.ship ?? 0) * (this.total ?? 0)) / 100)
        }
        else price = this.ship ?? 0
        return price
    }

    syncTotal(): void {
        let total = 0
        for (const line of this.lines) if (line.total !== undefined) total += line.total
        this.total = total
    }

    syncSalesTax(): void {
        let applyTax = false
        if (this.lines.length > 0) {
            for (const line of this.lines) {
                if (line.taxable) {
                    applyTax = true
                    break
                }
            }
        }
        if (applyTax && this.vendor!.allowSalesTax && this.taxcode === undefined) {
            const taxcode = this.vendor!.taxcode
            this.tax = taxcode !== undefined ? taxcode.rate : undefined
            this.nexus = taxcode !== undefined ? 'pro' : undefined
            this.taxcode = taxcode
        }
    }

    get taxableAmount(): number | undefined {
        const taxcode = this.taxcode
        let taxableAmount = taxcode !== undefined ? this.amount : 0
        if (this.lines.length > 0 && taxcode !== undefined) {
            taxableAmount = 0
            let total = 0
            for (const line of this.lines) {
                total += line.amount ?? 0
                if (line.taxable && line.amount !== undefined) taxableAmount += line.amount
            }
            if (this.shipTaxable && this.shipAmount && this.shipAmount > 0)
                taxableAmount += this.shipAmount
            if (this.overrideTotal && this.amount !== undefined && total > 0)
                taxableAmount = currencyFloat((taxableAmount * this.amount) / total)
        }
        return taxableAmount
    }

    get taxAmount(): number | undefined {
        if (this.id !== undefined) return this.tax
        if (this.tax !== undefined && this.amount !== undefined) {
            const taxAmount = currencyFloat(this.tax * (this.taxableAmount ?? 0) * 0.01)
            return this.paidDepositTax && this.paidDepositTax > 0
                ? Math.max(0.0, taxAmount - this.paidDepositTax)
                : taxAmount
        }
        return undefined
    }

    get surchargeAmount(): number | undefined {
        if (this.allowSurcharge) {
            let surchargeAmount =
                ((this.taxAmount ?? 0) +
                    (this.amount ?? 0) +
                    (this.gratuityAmount ?? 0) +
                    (this.shipAmount ?? 0)) *
                (Source.surchargeRate(this.source) ?? 0)
            if (
                this.source !== undefined &&
                this.source.type === 'checking' &&
                this.limitSurcharge &&
                surchargeAmount !== undefined &&
                surchargeAmount > Payment.ACH_PROCESSING_FEE_LIMIT
            )
                surchargeAmount = Payment.ACH_PROCESSING_FEE_LIMIT
            return surchargeAmount
        }
        return undefined
    }

    get discountAmount(): number | undefined {
        if (this.discount && this.discount > 0) {
            let discount = this.discount
            const total = this.discountableTotal!
            if (this.discountType === 'percent')
                discount = currencyFloat((this.discount * (total !== undefined ? total : 0)) / 100)
            else if (discount > total) discount = total
            return discount
        }
        return 0
    }

    get discountableTotal(): number | undefined {
        let discountableTotal = this.total
        if (this.lines.length > 0) {
            let total = 0
            discountableTotal = 0
            for (const line of this.lines) {
                total += line.total ?? 0
                if (line.discountable && line.total !== undefined) discountableTotal += line.total
            }
            if (this.overrideTotal && this.total !== undefined && total > 0)
                discountableTotal = currencyFloat((discountableTotal * this.total) / total)
        }
        return discountableTotal
    }

    get chargeAmount(): number | undefined {
        return (
            (this.amount ?? 0) +
            (this.taxAmount ?? 0) +
            (this.gratuityAmount ?? 0) +
            (this.shipAmount ?? 0) +
            (this.surchargeAmount ?? 0)
        )
    }

    get date(): moment.Moment | undefined {
        return this.createdDate
    }

    get paidDate(): moment.Moment | undefined {
        return this.createdDate
    }

    get description(): string | undefined {
        return this.type === 'cancel' || this.type === 'deposit' ? this.memo : this.refnum
    }

    static payoutDate(date?: moment.Moment): moment.Moment | undefined {
        if (date?.isBefore(moment().startOf('hour').add(1, 'hour'))) {
            // todo: figure out why .tz doesn't work, may need to import from moment-timezone
            // date = moment(moment().tz('America/Los_Angeles').format('YYYY-MM-DD') + ' 12:00:00', 'YYYY-MM-DD HH:mm:ss')
            date = moment(moment().format('YYYY-MM-DD') + ' 12:00:00', 'YYYY-MM-DD HH:mm:ss')
            if (date.isBefore(moment())) date.add(1, 'day')
            if ([6, 0].includes(date.day())) date.add(date.day() === 6 ? 2 : 1, 'day')
        }
        return date
    }

    static refundReasonStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'cancel', label: 'Services not rendered' }))
        strings.push(new Option(undefined, { id: 'overcharge', label: 'Overcharged' }))
        strings.push(new Option(undefined, { id: 'courtesy', label: 'Courtesy refund' }))
        strings.push(new Option(undefined, { id: 'paid', label: 'Client already paid' }))
        return strings
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.invoice) this.invoice = BaseRecord.fromSerializer(data.invoice, Invoice)
        if (data.booking) this.booking = BaseRecord.fromSerializer(data.booking, Booking)
        if (data.deposit) this.deposit = BaseRecord.fromSerializer(data.deposit, Deposit)
        if (data.packageRef) this.packageRef = BaseRecord.fromSerializer(data.packageRef, Package)
        if (data.royalty) this.royalty = BaseRecord.fromSerializer(data.royalty, Royalty)
        if (data.estimate) this.estimate = BaseRecord.fromSerializer(data.estimate, Estimate)
        if (data.order) this.order = BaseRecord.fromSerializer(data.order, Order)
        if (data.subscription)
            this.subscription = BaseRecord.fromSerializer(data.subscription, Subscription)
        if (data.gift) this.gift = BaseRecord.fromSerializer(data.gift, Gift)
        if (data._checkoutGift)
            this.checkoutGift = BaseRecord.fromSerializer(data.checkoutGift, Gift)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data.markPaidDate) this.markPaidDate = moment(data.markPaidDate)
    }

    static fk: FKType = mergeFk(Transaction.fk, {
        sources: ['source'],
        invoices: ['invoice'],
        bookings: ['booking'],
        deposits: ['deposit'],
        packages: ['aPackage'],
        royalties: ['royalty'],
        estimates: ['estimate'],
        orders: ['order'],
        subscriptions: ['subscription'],
        gifts: ['gift', 'checkoutGift'],
    })

    static dataType(): DataType {
        return 'payments'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        const allowAnyPayment =
            (this.invoice !== undefined && !!this.invoice.allowAnyPayment) ||
            (this.aPackage !== undefined && !!this.aPackage.allowAnyPayment)
        if (
            !(
                this.total &&
                this.total > 0 &&
                (this.pos || this.type === 'payment' || allowAnyPayment)
            )
        ) {
            delete dict['total']
        }
        if (this.gratuity) {
            dict['tip'] = this.gratuity
        }
        if (!!this.gratuityType) {
            dict['tip_type'] = this.gratuityType
        }
        dict['allow_surcharge'] = this.allowSurcharge
        dict['discount'] = this.discount && this.discount > 0 ? this.discount : ''
        dict['discount_type'] =
            this.discount && this.discount > 0 && this.discountType ? this.discountType : ''
        dict['is_pos'] = this.pos
        if (this.markPaidDate) {
            dict['mark_paid_date'] = formatTimeStamp(this.markPaidDate)
        }
        if (this.sendReceipt) {
            dict['send_receipt'] = true
        }
        if (this.source) {
            dict['source'] = this.source.id
        }
        else if (this.intent) {
            dict['intent'] = this.intent
        }
        if (this.latitude) {
            dict['latitude'] = this.latitude
        }
        if (this.longitude) {
            dict['longitude'] = this.longitude
        }
        if (this.invoice) {
            dict['invoice'] = this.invoice.id
        }
        if (this.booking) {
            dict['booking'] = this.booking.id
        }
        if (this.estimate) {
            dict['estimate'] = this.estimate.id
        }
        if (this.gift) {
            dict['gift'] = this.gift.id
        }
        if (this.aPackage) {
            dict['package'] = this.aPackage.id
        }
        if (this.royalty) {
            dict['royalty'] = this.royalty.id
        }
        if (this.order) {
            dict['order'] = this.order.id
        }
        if (this.memo) {
            dict['memo'] = this.memo
        }
        if (this.lines) {
            const jsonLines = []
            for (const line of this.lines) {
                jsonLines.push(line.jsonObject())
            }
            dict['lines'] = jsonLines
        }
        else {
            dict['lines'] = []
        }
        return dict
    }

    get invoice(): Invoice | undefined {
        return this.getForeignRecord('invoice', Invoice)
    }
    set invoice(invoice: Invoice | undefined) {
        this.setForeignRecord('invoice', invoice)
    }
    get booking(): Booking | undefined {
        return this.getForeignRecord('booking', Booking)
    }
    set booking(booking: Booking | undefined) {
        this.setForeignRecord('booking', booking)
    }
    get deposit(): Deposit | undefined {
        return this.getForeignRecord('deposit', Deposit)
    }
    set deposit(deposit: Deposit | undefined) {
        this.setForeignRecord('deposit', deposit)
    }
    get packageRef(): Package | undefined {
        return this.getForeignRecord('package', Package)
    }
    set packageRef(packageRef: Package | undefined) {
        this.setForeignRecord('package', packageRef)
    }
    get estimate(): Estimate | undefined {
        return this.getForeignRecord('estimate', Estimate)
    }
    set estimate(estimate: Estimate | undefined) {
        this.setForeignRecord('estimate', estimate)
    }
    get royalty(): Royalty | undefined {
        return this.getForeignRecord('royalty', Royalty)
    }
    set royalty(royalty: Royalty | undefined) {
        this.setForeignRecord('royalty', royalty)
    }
    get subscription(): Subscription | undefined {
        return this.getForeignRecord('subscription', Subscription)
    }
    set subscription(subscription: Subscription | undefined) {
        this.setForeignRecord('subscription', subscription)
    }
    get gift(): Gift | undefined {
        return this.getForeignRecord('gift', Gift)
    }
    set gift(gift: Gift | undefined) {
        this.setForeignRecord('gift', gift)
    }
    get source(): Source | undefined {
        return this.getSourceRecord('source')
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
}
